// src/components/common/GlobalSearch.js — Maxula
// Barre de recherche de la topbar = NAVIGATEUR GLOBAL du tableau de bord.
//  - PAGES : recherche instantanée (accents/casse/préfixe/sous-chaîne) -> suggestions.
//  - DONNÉES : selon le scope.
//      * merchant : recherche UNIFIÉE (deals, commandes, coupons, codes promo)
//        via /merchant/me/search — chaque résultat pointe vers le bon écran.
//      * admin / public : deals.
//  scope = 'admin' | 'merchant' | 'public'

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiX, FiLoader, FiCornerDownLeft, FiArrowRight, FiShoppingBag, FiTag, FiGift, FiPackage } from 'react-icons/fi';
import API from '../../services/api';
import { normalizeImagePath } from '../../utils/imageUtils';
import { searchPages } from '../../config/dashboardNav';

const formatMAD = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `${n.toLocaleString('fr-MA')} MAD`;
};

const dealThumb = (deal) => {
  const raw = (Array.isArray(deal?.images) && deal.images[0]) || deal?.image || deal?.thumbnail || null;
  return raw ? normalizeImagePath(raw) : null;
};

const pickList = (data) => data?.data || data?.deals || data?.items || (Array.isArray(data) ? data : []);

// Libellés de statut commande (compacts).
const ORDER_STATUS = {
  pending: 'En attente', confirmed: 'Confirmée', processing: 'En préparation',
  shipped: 'Expédiée', delivered: 'Livrée', completed: 'Terminée', cancelled: 'Annulée'
};

const GlobalSearch = ({ scope = 'public', placeholder = 'Rechercher une page, un deal, une commande, un coupon…', className = '' }) => {
  const [q, setQ] = useState('');
  const [cats, setCats] = useState([]); // catégories de résultats "données"
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const term = q.trim();

  // PAGES : recherche instantanée (synchrone) sur l'index du dashboard.
  const pages = useMemo(() => (scope === 'public' ? [] : searchPages(scope, term, 6)), [scope, term]);

  // DONNÉES : construit des catégories selon le scope.
  const fetchCats = useCallback(async (t) => {
    if (scope === 'merchant') {
      const { data } = await API.get('/merchant/me/search', { params: { q: t } });
      const d = (data && data.data) || {};
      const out = [];
      if (Array.isArray(d.deals) && d.deals.length) {
        out.push({
          key: 'deals', title: 'Deals', icon: FiPackage,
          items: d.deals.map((x) => ({
            id: `deal-${x.id}`, label: x.title || 'Deal', sub: formatMAD(x.discountedPrice ?? x.price),
            thumb: dealThumb(x), to: `/merchant/deals/${x.id}/edit`
          }))
        });
      }
      if (Array.isArray(d.orders) && d.orders.length) {
        out.push({
          key: 'orders', title: 'Commandes', icon: FiShoppingBag,
          items: d.orders.map((x) => ({
            id: `order-${x.id}`, label: `Commande ${x.orderNumber}`,
            sub: [ORDER_STATUS[x.status] || x.status, formatMAD(x.total)].filter(Boolean).join(' · '),
            to: `/merchant/orders?order=${encodeURIComponent(x.id)}`
          }))
        });
      }
      if (Array.isArray(d.vouchers) && d.vouchers.length) {
        out.push({
          key: 'vouchers', title: 'Coupons', icon: FiTag,
          items: d.vouchers.map((x) => ({
            id: `voucher-${x.id}`, label: x.code,
            sub: x.status === 'used' ? 'Utilisé' : (x.status === 'active' ? 'Actif' : x.status),
            to: `/merchant/vouchers?q=${encodeURIComponent(x.code)}`
          }))
        });
      }
      if (Array.isArray(d.promos) && d.promos.length) {
        out.push({
          key: 'promos', title: 'Codes promo', icon: FiGift,
          items: d.promos.map((x) => ({
            id: `promo-${x.id}`, label: x.code,
            sub: x.type === 'percentage' ? `${x.value}%` : formatMAD(x.value),
            to: '/merchant/promos'
          }))
        });
      }
      return out;
    }

    if (scope === 'admin') {
      const { data } = await API.get('/admin/deals', { params: { q: t, limit: 5 } });
      const items = pickList(data).slice(0, 5).map((x) => ({
        id: `deal-${x._id || x.id}`, label: x.title || 'Deal', sub: formatMAD(x.discountedPrice ?? x.price),
        thumb: dealThumb(x), to: `/deals/${x.slug || x._id || x.id}`
      }));
      return items.length ? [{ key: 'deals', title: 'Deals', icon: FiPackage, items }] : [];
    }

    const { data } = await API.get('/deals', { params: { search: t, limit: 6 } });
    const items = pickList(data).slice(0, 6).map((x) => ({
      id: `deal-${x._id || x.id}`, label: x.title || 'Deal', sub: formatMAD(x.discountedPrice ?? x.price),
      thumb: dealThumb(x), to: `/deals/${x.slug || x._id || x.id}`
    }));
    return items.length ? [{ key: 'deals', title: 'Deals', icon: FiPackage, items }] : [];
  }, [scope]);

  useEffect(() => {
    if (term.length < 2) { setCats([]); setLoading(false); return undefined; }
    let alive = true;
    setLoading(true);
    setOpen(true);
    const timer = setTimeout(async () => {
      try {
        const list = await fetchCats(term);
        if (alive) setCats(Array.isArray(list) ? list : []);
      } catch (err) {
        if (alive) setCats([]);
      } finally {
        if (alive) { setLoading(false); setActive(-1); }
      }
    }, 280);
    return () => { alive = false; clearTimeout(timer); };
  }, [term, fetchCats]);

  // Fermeture au clic extérieur
  useEffect(() => {
    const onDocClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const close = () => { setOpen(false); setQ(''); };

  const goTo = useCallback((to) => { if (!to) return; close(); navigate(to); }, [navigate]);
  const goToPage = useCallback((page) => { close(); navigate(page.path); }, [navigate]);

  const goToAllResults = useCallback(() => {
    if (!term) return;
    close();
    if (scope === 'admin') return navigate(`/admin/deals?q=${encodeURIComponent(term)}`);
    if (scope === 'merchant') return navigate(`/merchant/deals?q=${encodeURIComponent(term)}`);
    return navigate(`/deals?search=${encodeURIComponent(term)}`);
  }, [term, navigate, scope]);

  // Liste plate pour la navigation clavier : pages puis tous les items de données.
  const flatData = useMemo(() => cats.flatMap((c) => c.items), [cats]);
  const items = useMemo(
    () => [...pages.map((p) => ({ type: 'page', data: p })), ...flatData.map((d) => ({ type: 'data', data: d }))],
    [pages, flatData]
  );

  const runItem = (it) => {
    if (!it) return;
    if (it.type === 'page') goToPage(it.data);
    else goTo(it.data.to);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((i) => Math.min(i + 1, items.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, -1)); return; }
    if (e.key === 'Enter') {
      if (active >= 0 && items[active]) runItem(items[active]);
      else if (items[0]) runItem(items[0]);
      else goToAllResults();
    }
  };

  const showDropdown = open && term.length >= 2;
  const nothing = pages.length === 0 && flatData.length === 0 && !loading;

  return (
    <div ref={boxRef} className={`relative w-full ${className}`}>
      <div className={`flex items-center gap-2 px-4 h-11 rounded-full border transition-colors bg-gray-50 ${open ? 'border-primary-400 ring-2 ring-primary-100 bg-white' : 'border-gray-200'}`}>
        <FiSearch className="text-gray-400 shrink-0" size={18} aria-hidden="true" />
        <input
          ref={inputRef}
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => term.length >= 2 && setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          aria-label="Recherche"
          className="flex-1 bg-transparent outline-none text-sm text-gray-700 placeholder-gray-400"
        />
        {loading && <FiLoader className="text-primary-500 animate-spin shrink-0" size={16} />}
        {!loading && q && (
          <button type="button" onClick={() => { setQ(''); setCats([]); inputRef.current?.focus(); }} className="text-gray-400 hover:text-gray-600 shrink-0" aria-label="Effacer">
            <FiX size={16} />
          </button>
        )}
      </div>

      {showDropdown && (
        <div className="absolute left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50 max-h-[70vh] overflow-y-auto">
          {nothing && (
            <div className="px-4 py-6 text-center text-sm text-gray-400">Aucun résultat pour « {term} »</div>
          )}

          {pages.length > 0 && (
            <div className="py-1">
              <p className="px-4 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">Pages</p>
              <ul>
                {pages.map((p, idx) => {
                  const Icon = p.icon;
                  const gi = idx;
                  return (
                    <li key={p.path}>
                      <button type="button" onMouseEnter={() => setActive(gi)} onClick={() => goToPage(p)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${active === gi ? 'bg-primary-50' : 'hover:bg-gray-50'}`}>
                        <span className="w-9 h-9 rounded-lg bg-primary-50 text-primary-600 shrink-0 flex items-center justify-center">
                          {Icon ? <Icon size={18} /> : <FiArrowRight size={18} />}
                        </span>
                        <span className="text-sm font-medium text-gray-800 truncate">{p.label}</span>
                        <FiArrowRight className="ml-auto text-gray-300 shrink-0" size={15} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {cats.map((cat) => {
            const CatIcon = cat.icon || FiPackage;
            // offset global = pages + items des catégories précédentes
            let offset = pages.length;
            for (const c of cats) { if (c.key === cat.key) break; offset += c.items.length; }
            return (
              <div key={cat.key} className="py-1 border-t border-gray-100">
                <p className="px-4 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">{cat.title}</p>
                <ul>
                  {cat.items.map((it, idx) => {
                    const gi = offset + idx;
                    return (
                      <li key={it.id}>
                        <button type="button" onMouseEnter={() => setActive(gi)} onClick={() => goTo(it.to)}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${active === gi ? 'bg-primary-50' : 'hover:bg-gray-50'}`}>
                          <span className="w-9 h-9 rounded-lg bg-gray-100 overflow-hidden shrink-0 flex items-center justify-center">
                            {it.thumb ? (
                              <img src={it.thumb} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} className="w-full h-full object-cover" />
                            ) : (
                              <CatIcon className="text-gray-400" size={16} />
                            )}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-medium text-gray-800 truncate">{it.label}</span>
                            {it.sub && <span className="block text-xs text-primary-600 font-semibold truncate">{it.sub}</span>}
                          </span>
                          <FiArrowRight className="ml-auto text-gray-300 shrink-0" size={15} />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}

          {scope !== 'public' && (flatData.length > 0 || loading) && (
            <button type="button" onClick={goToAllResults}
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-primary-600 border-t border-gray-100 hover:bg-primary-50 transition-colors">
              <span>Voir tous les deals « {term} »</span>
              <FiCornerDownLeft size={16} aria-hidden="true" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
